import { FileText, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { dateiAdresse } from '../directus/client';
import { Karte } from '../komponenten/Karte';
import { NochNichtsGepflegt } from '../komponenten/Zustaende';
import type { Glossareintrag, Inhalte } from '../directus/typen';

/** HTML aus dem Rich-Text-Feld auf reinen Text bringen, allein zum Suchen -
 *  die Anzeige selbst bleibt formatiert. */
function alsSuchtext(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').toLowerCase();
}

export function Glossar({ inhalte }: { inhalte: Inhalte }) {
  const [suche, setSuche] = useState('');

  const treffer = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (q === '') return inhalte.glossar;
    return inhalte.glossar.filter((g) => {
      const inBegriff = g.begriff.toLowerCase().includes(q);
      const inErklaerung = g.erklaerung !== null && alsSuchtext(g.erklaerung).includes(q);
      const inRechtsgrundlage =
        g.rechtsgrundlage !== null && g.rechtsgrundlage.toLowerCase().includes(q);
      return inBegriff || inErklaerung || inRechtsgrundlage;
    });
  }, [inhalte.glossar, suche]);

  if (inhalte.glossar.length === 0) {
    return (
      <NochNichtsGepflegt
        was="Im Glossar"
        hinweis="Die Stufenleitung kann Begriffe in Directus anlegen; sie erscheinen dann sofort hier."
      />
    );
  }

  return (
    <div>
      <div
        className="mb-4 flex items-center gap-2 rounded-2xl bg-white px-4"
        style={{ minHeight: 56 }}
      >
        <Search size={19} className="shrink-0 text-marke-grau" aria-hidden="true" />
        <input
          value={suche}
          onChange={(e) => {
            setSuche(e.target.value);
          }}
          placeholder="Begriff suchen, zum Beispiel Verweis oder Ausgleich"
          className="flex-1 bg-transparent py-3 text-base text-marke-tinte outline-none placeholder:text-marke-grau"
          type="search"
          aria-label="Glossar durchsuchen"
        />
      </div>

      {treffer.length === 0 ? (
        <Karte className="p-6 text-center">
          <div className="text-sm text-marke-grau">
            Kein Eintrag gefunden. Versuche ein anderes Stichwort.
          </div>
        </Karte>
      ) : (
        <div className="space-y-2.5">
          {treffer.map((g) => (
            <GlossarEintrag key={g.id} eintrag={g} />
          ))}
        </div>
      )}
    </div>
  );
}

function GlossarEintrag({ eintrag }: { eintrag: Glossareintrag }) {
  return (
    <Karte className="p-4">
      <div className="text-base font-bold text-marke-tinte">{eintrag.begriff}</div>
      {eintrag.erklaerung !== null && (
        <div
          className="mt-1 text-sm text-marke-grau [&_p]:mt-2 [&_p:first-child]:mt-0"
          // Die Redaktion pflegt diesen Text ueber Directus, nicht ein
          // beliebiger Websitebesucher - dasselbe Vertrauensmodell wie beim
          // Rich-Text-Feld selbst. Siehe Datenschutz-Abschnitt im README.
          dangerouslySetInnerHTML={{ __html: eintrag.erklaerung }}
        />
      )}

      {(eintrag.rechtsgrundlage !== null || eintrag.dokument !== null) && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {eintrag.dokument !== null &&
            (eintrag.dokument.datei !== null ? (
              <a
                href={dateiAdresse(eintrag.dokument.datei)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 rounded-lg bg-marke-blau/10 px-2.5 py-1.5 text-xs font-medium text-marke-blau transition-opacity hover:opacity-80"
              >
                <FileText size={12} aria-hidden="true" />
                {eintrag.dokument.titel}
              </a>
            ) : (
              <span className="flex items-center gap-1 rounded-lg bg-marke-flaeche px-2.5 py-1.5 text-xs font-medium text-marke-grau">
                <FileText size={12} aria-hidden="true" />
                {eintrag.dokument.titel}
              </span>
            ))}
          {eintrag.rechtsgrundlage !== null && (
            <span className="rounded-lg bg-marke-flaeche px-2.5 py-1.5 text-xs font-medium text-marke-grau">
              {eintrag.rechtsgrundlage}
            </span>
          )}
        </div>
      )}
    </Karte>
  );
}
